import type { Logger, Unsubscribe } from '../core/services';
import type {
  Recorder,
  RecorderStartOptions,
  RecorderState,
  RecordingFormat,
  RecordingResult,
} from './api';
import { extensionForMime, listSupportedFormats } from './formats';
import { openDurableSink, openWritableSink, workersSupported, type ChunkSink } from './opfsSink';
import { createRecordingMetaStore, type StoredRecording } from './store';

const TIMESLICE_MS = 1000;
const RECORDINGS_DIR = 'recordings';
const DOWNLOAD_REVOKE_DELAY_MS = 60_000;
const META_UPDATE_INTERVAL_MS = 5000;
const QUOTA_CHECK_INTERVAL_MS = 5000;
/** Refuse to start with less free space than this. */
const MIN_FREE_TO_START = 16 * 1024 * 1024;
/** Stop cleanly, keeping the file, when free space falls below this. */
const MIN_FREE_WHILE_RECORDING = 8 * 1024 * 1024;
/** Without Web Locks, a 'recording' row untouched for this long is treated as abandoned. */
const STALE_ROW_MS = 30_000;
const MIME_BY_EXTENSION: Record<string, string> = { webm: 'video/webm', mp4: 'video/mp4', mkv: 'video/x-matroska', ogg: 'audio/ogg' };

const lockName = (id: string): string => `ohmyobs-recording-${id}`;

interface ShowSaveFilePickerOptions {
  suggestedName?: string;
  types?: Array<{ description?: string; accept: Record<string, string[]> }>;
}
type SavePicker = (options?: ShowSaveFilePickerOptions) => Promise<FileSystemFileHandle>;

interface Session {
  id: string;
  recorder: MediaRecorder;
  mimeType: string;
  extension: string;
  fileName: string;
  sink: 'memory' | 'opfs';
  createdAt: number;
  file: ChunkSink | null;
  releaseLock: (() => void) | null;
  lastMetaAt: number;
  lastQuotaAt: number;
  chunks: Blob[];
  writeChain: Promise<void>;
  /** First write failure; later chunks are discarded, the recorder is stopped and what was flushed is kept. */
  writeError: string | null;
  error: string | null;
  bytesWritten: number;
  accumulatedMs: number;
  runningSince: number | null;
  stopRequested: boolean;
  finalizing: boolean;
  tracks: MediaStreamTrack[];
  detachTracks: () => void;
  started: Promise<void>;
  finished: Promise<RecordingResult>;
  resolveFinished: (r: RecordingResult) => void;
  rejectFinished: (e: Error) => void;
}

const IDLE: RecorderState = { status: 'idle', startedAt: null, durationMs: 0, bytesWritten: 0, mimeType: null, error: null };

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function safeBaseName(name: string): string {
  const cleaned = name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').trim().replace(/^\.+/, '');
  return cleaned === '' ? 'recording' : cleaned.slice(0, 120);
}

function newId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
}

export function createRecorder(deps: { logger: Logger }): Recorder {
  const log = (level: 'info' | 'warn' | 'error', message: string, detail?: unknown) =>
    deps.logger.log(level, 'recorder', message, detail);

  const meta = createRecordingMetaStore();
  const memory = new Map<string, { result: RecordingResult; blob: Blob }>();
  const listeners = new Set<(state: RecorderState) => void>();
  let session: Session | null = null;
  /** Id of a recording that is being set up; its row exists before the session does. */
  let startingId: string | null = null;
  /** Result of a recording that ended on its own (error or input loss) and has not been collected via stop(). */
  let orphanResult: RecordingResult | null = null;
  let status: RecorderState['status'] = 'idle';
  let startedAt: number | null = null;
  let lastError: string | null = null;
  let frozen: Pick<RecorderState, 'durationMs' | 'bytesWritten' | 'mimeType'> | null = null;
  let opfsDir: FileSystemDirectoryHandle | null | undefined;

  const opfsSupported = (): boolean =>
    typeof navigator !== 'undefined' &&
    typeof navigator.storage?.getDirectory === 'function' &&
    typeof FileSystemFileHandle !== 'undefined' &&
    'createWritable' in FileSystemFileHandle.prototype;

  /** null = OPFS not offered by this browser. Throws on transient failures, which are not cached. */
  const getOpfsDir = async (): Promise<FileSystemDirectoryHandle | null> => {
    if (opfsDir !== undefined) return opfsDir;
    if (!opfsSupported()) return (opfsDir = null);
    const root = await navigator.storage.getDirectory();
    return (opfsDir = await root.getDirectoryHandle(RECORDINGS_DIR, { create: true }));
  };

  const freeBytes = async (): Promise<number | null> => {
    try {
      const { quota, usage } = await navigator.storage.estimate();
      return quota === undefined || usage === undefined ? null : quota - usage;
    } catch {
      return null;
    }
  };

  const elapsed = (s: Session): number => s.accumulatedMs + (s.runningSince === null ? 0 : performance.now() - s.runningSince);

  const getState = (): RecorderState => {
    if (session) {
      return {
        status,
        startedAt,
        durationMs: Math.round(elapsed(session)),
        bytesWritten: session.bytesWritten,
        mimeType: session.mimeType,
        error: lastError,
      };
    }
    return { ...IDLE, ...(frozen ?? {}), status, startedAt, error: lastError };
  };

  const emit = (): void => {
    const state = getState();
    for (const l of [...listeners]) {
      try {
        l(state);
      } catch (err) {
        log('error', `Recorder listener threw: ${messageOf(err)}`);
      }
    }
  };

  const setStatus = (next: RecorderState['status']): void => {
    status = next;
    emit();
  };

  const rowFor = (s: Session, state: StoredRecording['state'], sizeBytes: number): StoredRecording => ({
    id: s.id,
    fileName: s.fileName,
    mimeType: s.mimeType,
    durationMs: Math.round(elapsed(s)),
    sizeBytes,
    createdAt: s.createdAt,
    sink: 'opfs',
    fileExtension: s.extension,
    state,
    updatedAt: Date.now(),
  });

  function stopWithError(s: Session, message: string): void {
    s.error ??= message;
    log('error', message);
    if (s.recorder.state !== 'inactive') s.recorder.stop();
  }

  /** Cheap periodic bookkeeping while a recording grows: metadata row and free-space guard. */
  function housekeeping(s: Session): void {
    const now = Date.now();
    if (now - s.lastMetaAt >= META_UPDATE_INTERVAL_MS) {
      s.lastMetaAt = now;
      meta.put(rowFor(s, 'recording', s.bytesWritten)).catch((err) => log('warn', `Could not update recording metadata: ${messageOf(err)}`));
    }
    if (now - s.lastQuotaAt >= QUOTA_CHECK_INTERVAL_MS) {
      s.lastQuotaAt = now;
      void freeBytes().then((free) => {
        if (free !== null && free < MIN_FREE_WHILE_RECORDING && !s.finalizing) {
          stopWithError(s, 'Storage is almost full; recording stopped and what was saved has been kept');
        }
      });
    }
  }

  function enqueueWrite(s: Session, chunk: Blob): void {
    if (s.sink === 'memory') {
      s.chunks.push(chunk);
      s.bytesWritten += chunk.size;
      emit();
      return;
    }
    s.writeChain = s.writeChain.then(async () => {
      if (s.writeError || !s.file) return;
      try {
        await s.file.write(chunk);
        s.bytesWritten += chunk.size;
        emit();
        housekeeping(s);
      } catch (err) {
        s.writeError = `Writing the recording failed: ${messageOf(err)}`;
        stopWithError(s, s.writeError);
      }
    });
  }

  async function finalize(s: Session): Promise<void> {
    if (s.finalizing) return;
    s.finalizing = true;
    s.detachTracks();
    if (s.runningSince !== null) {
      s.accumulatedMs += performance.now() - s.runningSince;
      s.runningSince = null;
    }
    if (!s.stopRequested && !s.error) s.error = 'The recorder stopped because its input ended';
    if (status !== 'error') setStatus('stopping');

    try {
      await s.writeChain;
      let size: number;
      if (s.sink === 'opfs' && s.file) {
        try {
          size = await s.file.close();
        } catch (err) {
          s.error ??= `Finalising the recording file failed: ${messageOf(err)}`;
          log('error', s.error);
          size = s.file.durable ? s.bytesWritten : 0;
        }
      } else {
        size = s.chunks.reduce((n, c) => n + c.size, 0);
      }

      if (size === 0) {
        if (s.sink === 'opfs') await removeFile(`${s.id}.${s.extension}`);
        if (s.sink === 'opfs') await meta.delete(s.id).catch(() => undefined);
        throw new Error(s.error ?? 'Nothing was recorded');
      }

      const result: RecordingResult = {
        id: s.id,
        fileName: s.fileName,
        mimeType: s.mimeType,
        durationMs: Math.round(s.accumulatedMs),
        sizeBytes: size,
        createdAt: s.createdAt,
        sink: s.sink,
      };
      if (s.sink === 'opfs') {
        await meta.put({ ...result, fileExtension: s.extension, state: 'complete', updatedAt: Date.now() });
      } else {
        memory.set(s.id, { result, blob: new Blob(s.chunks, { type: s.mimeType }) });
      }

      frozen = { durationMs: result.durationMs, bytesWritten: size, mimeType: s.mimeType };
      session = null;
      if (s.error) {
        lastError = s.error;
        orphanResult = result;
        status = 'error';
      } else {
        lastError = null;
        frozen = null;
        startedAt = null;
        status = 'idle';
      }
      s.releaseLock?.();
      emit();
      s.resolveFinished(result);
    } catch (err) {
      const failure = err instanceof Error ? err : new Error(String(err));
      session = null;
      lastError = failure.message;
      frozen = { durationMs: Math.round(s.accumulatedMs), bytesWritten: s.bytesWritten, mimeType: s.mimeType };
      status = 'error';
      s.releaseLock?.();
      emit();
      s.rejectFinished(failure);
    }
  }

  function noteTrackEnded(s: Session): void {
    const live = s.tracks.filter((t) => t.readyState === 'live');
    const videoTracks = s.tracks.filter((t) => t.kind === 'video');
    const videoGone = videoTracks.length > 0 && videoTracks.every((t) => t.readyState === 'ended');
    if (live.length > 0 && !videoGone) {
      log('warn', 'An input track ended during recording; continuing with the remaining tracks');
      return;
    }
    s.error ??= 'The recorded stream ended';
    if (s.runningSince !== null) {
      s.accumulatedMs += performance.now() - s.runningSince;
      s.runningSince = null;
    }
    if (s.recorder.state !== 'inactive') s.recorder.stop();
  }

  async function removeFile(name: string): Promise<void> {
    try {
      await (await getOpfsDir())?.removeEntry(name);
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'NotFoundError')) log('warn', `Could not remove ${name}: ${messageOf(err)}`);
    }
  }

  /** Held only while a recording is live, so a crashed or closed tab releases it automatically. */
  function acquireLock(id: string): Promise<(() => void) | null> {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (!locks) return Promise.resolve(null);
    return new Promise((resolve) => {
      locks
        .request(lockName(id), () => new Promise<void>((release) => resolve(() => release())))
        .catch(() => resolve(null));
    });
  }

  async function heldLockNames(): Promise<Set<string> | null> {
    try {
      const snapshot = await navigator.locks?.query();
      return snapshot ? new Set((snapshot.held ?? []).map((l) => l.name ?? '')) : null;
    } catch {
      return null;
    }
  }

  async function openChunkSink(id: string, extension: string): Promise<ChunkSink> {
    const dir = await getOpfsDir();
    if (!dir) throw new Error('no OPFS');
    const name = `${id}.${extension}`;
    if (workersSupported()) {
      try {
        return await openDurableSink(RECORDINGS_DIR, name);
      } catch (err) {
        log('warn', `Crash-safe writer unavailable (${messageOf(err)}); using a buffered writer that only saves on stop`);
      }
    } else {
      log('warn', 'Workers are unavailable; the recording is only saved when it is stopped');
    }
    return openWritableSink(dir, name);
  }

  async function start(options: RecorderStartOptions): Promise<void> {
    if (session || startingId || status === 'starting' || status === 'stopping') throw new Error('A recording is already in progress');
    if (typeof MediaRecorder === 'undefined') throw new Error('MediaRecorder is not supported in this browser');
    const tracks = options.stream.getTracks().filter((t) => t.readyState === 'live');
    if (tracks.length === 0) throw new Error('The stream has no live tracks to record');

    lastError = null;
    frozen = null;
    orphanResult = null;
    setStatus('starting');

    const id = newId();
    startingId = id;
    let file: ChunkSink | null = null;
    let releaseLock: (() => void) | null = null;
    let rowWritten = false;
    let entryName = '';
    try {
      const supported = listSupportedFormats();
      let mimeType = options.mimeType;
      if (mimeType === 'auto') {
        mimeType = supported[0]?.mimeType ?? '';
      } else if (!MediaRecorder.isTypeSupported(mimeType)) {
        throw new Error(`This browser cannot record "${mimeType}"`);
      }
      const recorder = new MediaRecorder(options.stream, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: Math.round(options.videoKbps * 1000),
        audioBitsPerSecond: Math.round(options.audioKbps * 1000),
      });
      const actualMime = recorder.mimeType || mimeType || 'video/webm';
      const extension = extensionForMime(actualMime);
      entryName = `${id}.${extension}`;

      let sink: 'memory' | 'opfs' = options.sink;
      if (sink === 'opfs') {
        let dir: FileSystemDirectoryHandle | null = null;
        try {
          dir = await getOpfsDir();
        } catch (err) {
          log('warn', `OPFS could not be opened: ${messageOf(err)}`);
        }
        if (!dir) {
          log('warn', 'OPFS is unavailable; recording to memory instead');
          sink = 'memory';
        }
      }

      const createdAt = Date.now();
      const fileName = `${safeBaseName(options.fileName)}.${extension}`;
      if (sink === 'opfs') {
        const free = await freeBytes();
        if (free !== null && free < MIN_FREE_TO_START) {
          throw new Error(`Not enough free storage to start recording (${Math.floor(free / 1048576)} MiB available)`);
        }
        releaseLock = await acquireLock(id);
        // The row exists before the file so that any file on disk can be traced back to a recording.
        await meta.put({
          id, fileName, mimeType: actualMime, durationMs: 0, sizeBytes: 0, createdAt, sink: 'opfs',
          fileExtension: extension, state: 'recording', updatedAt: createdAt,
        });
        rowWritten = true;
        file = await openChunkSink(id, extension);
      }

      let resolveFinished!: (r: RecordingResult) => void;
      let rejectFinished!: (e: Error) => void;
      const finished = new Promise<RecordingResult>((res, rej) => {
        resolveFinished = res;
        rejectFinished = rej;
      });
      // A rejection nobody awaits (auto-finalised recordings) must not surface as an unhandled rejection.
      finished.catch(() => undefined);

      const s: Session = {
        id,
        recorder,
        mimeType: actualMime,
        extension,
        fileName,
        sink,
        createdAt,
        file,
        releaseLock,
        lastMetaAt: createdAt,
        lastQuotaAt: createdAt,
        chunks: [],
        writeChain: Promise.resolve(),
        writeError: null,
        error: null,
        bytesWritten: 0,
        accumulatedMs: 0,
        runningSince: null,
        stopRequested: false,
        finalizing: false,
        tracks,
        detachTracks: () => undefined,
        started: Promise.resolve(),
        finished,
        resolveFinished,
        rejectFinished,
      };

      const onEnded = () => noteTrackEnded(s);
      for (const t of tracks) t.addEventListener('ended', onEnded);
      s.detachTracks = () => {
        for (const t of tracks) t.removeEventListener('ended', onEnded);
        s.detachTracks = () => undefined;
      };

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) enqueueWrite(s, e.data);
      };
      recorder.onerror = (e: Event) => {
        const err = (e as Event & { error?: DOMException }).error;
        s.error ??= `Recorder error: ${err?.message || err?.name || 'unknown'}`;
        log('error', s.error);
        if (s.runningSince !== null) {
          s.accumulatedMs += performance.now() - s.runningSince;
          s.runningSince = null;
        }
        if (recorder.state !== 'inactive') recorder.stop();
      };
      recorder.onstop = () => void finalize(s);

      s.started = new Promise<void>((resolve, reject) => {
        recorder.addEventListener('start', () => resolve(), { once: true });
        recorder.addEventListener('error', () => reject(new Error(s.error ?? 'Recorder failed to start')), { once: true });
      });

      session = s;
      startedAt = Date.now();
      recorder.start(TIMESLICE_MS);
      await s.started;
      s.runningSince = performance.now();
      setStatus('recording');
      log('info', `Recording started (${actualMime}, ${s.sink}${file && !file.durable ? ', not crash-safe' : ''})`);
    } catch (err) {
      const failed = session as Session | null;
      session = null;
      failed?.detachTracks();
      if (failed?.recorder && failed.recorder.state !== 'inactive') {
        failed.recorder.onstop = null;
        failed.recorder.stop();
      }
      await file?.abort();
      if (rowWritten) {
        await removeFile(entryName);
        await meta.delete(id).catch(() => undefined);
      }
      releaseLock?.();
      lastError = messageOf(err);
      startedAt = null;
      setStatus('error');
      throw err;
    } finally {
      startingId = null;
    }
  }

  type FileLookup = { kind: 'found'; file: File } | { kind: 'missing' } | { kind: 'unavailable' };

  /** Only a NotFoundError for this very file counts as missing; anything else says nothing about the file. */
  async function lookupFile(name: string): Promise<FileLookup> {
    let dir: FileSystemDirectoryHandle | null;
    try {
      dir = await getOpfsDir();
    } catch {
      return { kind: 'unavailable' };
    }
    if (!dir) return { kind: 'unavailable' };
    try {
      return { kind: 'found', file: await (await dir.getFileHandle(name)).getFile() };
    } catch (err) {
      return err instanceof DOMException && err.name === 'NotFoundError' ? { kind: 'missing' } : { kind: 'unavailable' };
    }
  }

  /**
   * Adopts recordings whose tab died before a clean finish: rows still marked 'recording' that nobody holds a
   * lock for, and files that have no row at all. Their content is whatever had been flushed.
   */
  async function recoverOrphans(): Promise<void> {
    const held = await heldLockNames();
    const live = (id: string, updatedAt: number): boolean =>
      id === session?.id || id === startingId || (held ? held.has(lockName(id)) : Date.now() - updatedAt < STALE_ROW_MS);

    const rows = await meta.list();
    for (const row of rows) {
      if (row.state !== 'recording' || live(row.id, row.updatedAt)) continue;
      const found = await lookupFile(`${row.id}.${row.fileExtension}`);
      if (found.kind === 'unavailable') continue;
      if (found.kind === 'missing' || found.file.size === 0) {
        if (found.kind === 'found') await removeFile(`${row.id}.${row.fileExtension}`);
        await meta.delete(row.id);
        continue;
      }
      await meta.put({ ...row, state: 'complete', sizeBytes: found.file.size, recovered: true, updatedAt: Date.now() });
      log('info', `Recovered unfinished recording ${row.id} (${found.file.size} bytes)`);
    }

    let dir: FileSystemDirectoryHandle | null = null;
    try {
      dir = await getOpfsDir();
    } catch {
      return;
    }
    if (!dir) return;
    const known = new Set(rows.map((r) => r.id));
    for await (const [name, handle] of (dir as unknown as { entries(): AsyncIterable<[string, FileSystemHandle]> }).entries()) {
      // Only names we create: this skips the browser's own '.crswap' scratch files for open writable streams.
      const match = /^([A-Za-z0-9_-]{1,64})\.(webm|mp4|mkv|ogg)$/.exec(name);
      if (handle.kind !== 'file' || !match) continue;
      const [, id, extension] = match as unknown as [string, string, string];
      if (known.has(id) || live(id, 0)) continue;
      const file = await (handle as FileSystemFileHandle).getFile().catch(() => null);
      if (!file) continue;
      if (file.size === 0) {
        await removeFile(name);
        continue;
      }
      await meta.put({
        id, fileName: `recovered-${id}.${extension}`, mimeType: MIME_BY_EXTENSION[extension] ?? 'video/webm', durationMs: 0,
        sizeBytes: file.size, createdAt: file.lastModified, sink: 'opfs', fileExtension: extension, state: 'complete',
        updatedAt: Date.now(), recovered: true,
      });
      log('info', `Adopted unregistered recording file ${name} (${file.size} bytes)`);
    }
  }

  const recorder: Recorder = {
    listSupportedFormats: (): RecordingFormat[] => listSupportedFormats(),

    start,

    pause() {
      const s = session;
      if (!s || status !== 'recording' || s.recorder.state !== 'recording') return;
      s.recorder.pause();
      if (s.runningSince !== null) s.accumulatedMs += performance.now() - s.runningSince;
      s.runningSince = null;
      setStatus('paused');
    },

    resume() {
      const s = session;
      if (!s || status !== 'paused' || s.recorder.state !== 'paused') return;
      s.recorder.resume();
      s.runningSince = performance.now();
      setStatus('recording');
    },

    async stop() {
      const s = session;
      if (!s) {
        if (orphanResult) return orphanResult;
        throw new Error('No recording is in progress');
      }
      await s.started.catch(() => undefined);
      s.stopRequested = true;
      if (s.runningSince !== null) {
        s.accumulatedMs += performance.now() - s.runningSince;
        s.runningSince = null;
      }
      if (status !== 'stopping') setStatus('stopping');
      if (s.recorder.state !== 'inactive') s.recorder.stop();
      return s.finished;
    },

    getState,

    subscribe(listener): Unsubscribe {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },

    async listRecordings() {
      await recoverOrphans().catch((err) => log('warn', `Recovery scan failed: ${messageOf(err)}`));
      const out: RecordingResult[] = [...memory.values()].map((m) => m.result);
      for (const stored of await meta.list()) {
        if (stored.state !== 'complete') continue;
        const found = await lookupFile(`${stored.id}.${stored.fileExtension}`);
        if (found.kind === 'missing') {
          await meta.delete(stored.id);
          continue;
        }
        const { fileExtension: _ext, state: _state, updatedAt: _updated, recovered: _recovered, ...result } = stored;
        out.push(found.kind === 'found' ? { ...result, sizeBytes: found.file.size } : result);
      }
      return out.sort((a, b) => b.createdAt - a.createdAt);
    },

    async getRecordingBlob(id) {
      const mem = memory.get(id);
      if (mem) return mem.blob;
      const stored = await meta.get(id);
      if (!stored || stored.state !== 'complete') return null;
      const found = await lookupFile(`${stored.id}.${stored.fileExtension}`);
      return found.kind === 'found' ? new Blob([found.file], { type: stored.mimeType }) : null;
    },

    async download(id) {
      const blob = await recorder.getRecordingBlob(id);
      if (!blob) throw new Error('Recording not found');
      const rec = memory.get(id)?.result ?? (await meta.get(id));
      const fileName = rec?.fileName ?? `${id}.webm`;
      const picker = (globalThis as { showSaveFilePicker?: SavePicker }).showSaveFilePicker;
      if (typeof picker === 'function') {
        let handle: Awaited<ReturnType<SavePicker>> | null = null;
        try {
          handle = await picker({
            suggestedName: fileName,
            types: [{ description: 'Recording', accept: { [blob.type.split(';')[0] || 'video/webm']: [`.${fileName.split('.').pop()}`] } }],
          });
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          // Refused without a user gesture, in a cross-origin frame (the embedded studio) or by policy:
          // the anchor download below still works then.
          log('info', 'Save picker unavailable, downloading the file instead', err);
        }
        if (handle) {
          const writable = await handle.createWritable();
          await blob.stream().pipeTo(writable);
          return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.style.display = 'none';
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_REVOKE_DELAY_MS);
    },

    async deleteRecording(id) {
      if (memory.delete(id)) return;
      const stored = await meta.get(id);
      if (!stored) return;
      await removeFile(`${stored.id}.${stored.fileExtension}`);
      await meta.delete(id);
      if (orphanResult?.id === id) orphanResult = null;
    },

    async importRecording(blob, options) {
      if (blob.size === 0) throw new Error('Nothing to save');
      const id = newId();
      const mimeType = blob.type || 'video/webm';
      const extension = extensionForMime(mimeType);
      const fileName = `${safeBaseName(options.fileName)}.${extension}`;
      const createdAt = Date.now();
      const result: RecordingResult = {
        id, fileName, mimeType, durationMs: Math.round(options.durationMs), sizeBytes: blob.size, createdAt, sink: options.sink,
      };

      let dir: FileSystemDirectoryHandle | null = null;
      if (options.sink === 'opfs') {
        try {
          dir = await getOpfsDir();
        } catch (err) {
          log('warn', `OPFS could not be opened: ${messageOf(err)}`);
        }
        if (!dir) log('warn', 'OPFS is unavailable; keeping the clip in memory instead');
      }
      if (!dir) {
        memory.set(id, { result: { ...result, sink: 'memory' }, blob });
        return { ...result, sink: 'memory' };
      }

      const free = await freeBytes();
      if (free !== null && free < blob.size + MIN_FREE_WHILE_RECORDING) throw new Error('Not enough free storage to save the clip');
      // Same order as a live recording: locked row first, so recovery never adopts or deletes a half-written file.
      const releaseLock = await acquireLock(id);
      const entryName = `${id}.${extension}`;
      try {
        await meta.put({ ...result, fileExtension: extension, state: 'recording', updatedAt: createdAt });
        const file = await openWritableSink(dir, entryName);
        try {
          await file.write(blob);
        } catch (err) {
          await file.abort();
          throw err;
        }
        const sizeBytes = await file.close();
        const stored: RecordingResult = { ...result, sizeBytes };
        await meta.put({ ...stored, fileExtension: extension, state: 'complete', updatedAt: Date.now() });
        return stored;
      } catch (err) {
        await removeFile(entryName);
        await meta.delete(id).catch(() => undefined);
        throw new Error(`Saving the clip failed: ${messageOf(err)}`);
      } finally {
        releaseLock?.();
      }
    },
  };

  return recorder;
}
